import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import FavoriteIcon from "@mui/icons-material/Favorite";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { fetchFilmReel, ReelClip } from "./filmReelShare";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * FlagshipFeedPhone — a REAL swipeable feed of the sample reel (the interaction from Letter K's
 * "feedPhone"): a vertical scroll-snap feed, one card per clip, only the in-view card plays. It has
 * a swipe hint, a TikTok right rail (like · CherryPick · reframe), burned captions and sound.
 *
 * When `pickIds` is passed (the sibling editor phone's ticks, in order), the feed plays THOSE clips
 * as one stitched reel and auto-advances through them; otherwise it plays the whole reel. `reframe`
 * lets the visitor drag a clip to choose its 9:16 focus point. Clips + the one presigned video come
 * from fetchFilmReel(); lazy-loaded on scroll.
 */

const CHERRY = "#ec2b57";
const F = "Outfit, system-ui, -apple-system, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";

const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k` : `${n}`);

type Props = { heightPx?: number; pickIds?: number[] };

const FlagshipFeedPhone: React.FC<Props> = ({ heightPx = 610, pickIds }) => {
  const screenH = Math.max(360, heightPx);
  const screenW = Math.round(screenH * (288 / 610));

  const [near, setNear] = useState(false);
  const [clips, setClips] = useState<ReelClip[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [err, setErr] = useState(false);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState<Set<number>>(new Set());
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [framePos, setFramePos] = useState<Record<number, { x: number; y: number }>>({});
  const [reframeId, setReframeId] = useState<number | null>(null);
  const dragFrom = useRef<{ x: number; y: number } | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const cardEls = useRef<Map<number, HTMLDivElement>>(new Map());
  const videoEls = useRef<Map<number, HTMLVideoElement>>(new Map());
  const progressEls = useRef<Map<number, HTMLDivElement>>(new Map());
  const activeId = useRef<number | null>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    fetchFilmReel()
      .then(({ videoUrl: v, clips: cs }) => {
        if (cancelled) return;
        const good = cs.filter((c) => c.end - c.start >= 1.5);
        setVideoUrl(v);
        setClips((good.length >= 4 ? good : cs).slice(0, 12));
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [near]);

  // what the feed plays: the editor's picks (as one stitched reel) if provided, else the whole reel
  const feedList = useMemo(() => {
    if (pickIds && pickIds.length) {
      const byId = new Map(clips.map((c) => [c.id, c]));
      const list = pickIds.map((id) => byId.get(id)).filter(Boolean) as ReelClip[];
      if (list.length) return list;
    }
    return clips;
  }, [clips, pickIds]);
  const inReelMode = !!(pickIds && pickIds.length) && feedList !== clips && feedList.length > 0;

  // when the pick set changes, restart the feed from the top
  const reelKey = (pickIds || []).join(",");
  useEffect(() => {
    activeId.current = null;
    try { feedRef.current?.scrollTo({ top: 0 }); } catch (e) { /* ignore */ }
  }, [reelKey]);

  // play only the card in view; seek it to its clip start
  useEffect(() => {
    const rootEl = feedRef.current;
    if (!rootEl || !feedList.length) return;
    const startClip = (v: HTMLVideoElement, clip: ReelClip) => {
      const go = () => {
        try {
          if (Math.abs(v.currentTime - clip.start) > 0.25) {
            v.addEventListener("seeked", () => { v.play().catch(() => { /* autoplay denied */ }); }, { once: true });
            v.currentTime = clip.start;
          } else {
            v.play().catch(() => { /* autoplay denied */ });
          }
        } catch (e) { /* ignore */ }
      };
      if (v.readyState >= 1) go();
      else { v.preload = "auto"; v.addEventListener("loadedmetadata", go, { once: true }); try { v.load(); } catch (e) { /* ignore */ } }
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          let id: number | null = null;
          cardEls.current.forEach((el, key) => { if (el === en.target) id = key; });
          if (id == null) return;
          const v = videoEls.current.get(id);
          if (!en.isIntersecting) {
            if (v) { try { v.pause(); } catch (e) { /* ignore */ } }
            if (activeId.current === id) activeId.current = null;
            return;
          }
          if (activeId.current === id) return;
          const prev = activeId.current != null ? videoEls.current.get(activeId.current) : null;
          if (prev) { try { prev.pause(); } catch (e) { /* ignore */ } }
          activeId.current = id;
          const clip = feedList.find((c) => c.id === id);
          if (v && clip) startClip(v, clip);
        });
      },
      { root: rootEl, threshold: 0.6 },
    );
    cardEls.current.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [feedList]);

  const toggleMute = () => { const next = !muted; videoEls.current.forEach((v) => { v.muted = next; }); setMuted(next); };
  const like = (id: number) => setLiked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pick = (id: number) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const railBtn = (on: boolean, onClick: () => void, node: React.ReactNode, label: string) => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 52 }}>
      <Box component="button" onClick={onClick} sx={{ cursor: "pointer", width: 38, height: 38, borderRadius: "999px", border: `1px solid ${on ? CHERRY : "rgba(255,255,255,0.16)"}`, background: on ? `${CHERRY}33` : "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", p: 0, color: on ? CHERRY : "#fff", "&:hover": { borderColor: CHERRY } }}>
        {node}
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: "8.5px", color: "#fff", mt: "3px", whiteSpace: "nowrap" }}>{label}</Typography>
    </Box>
  );

  return (
    <Box ref={rootRef} sx={{ flexShrink: 0, p: "10px", borderRadius: "44px", background: "linear-gradient(160deg,#2a2733,#141219)", border: "1px solid rgba(255,255,255,0.14)", boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 90px -30px ${CHERRY}66` }}>
      <Box sx={{ position: "relative", width: screenW, height: screenH, borderRadius: "34px", overflow: "hidden", background: "#0c0b10" }}>
        <Box ref={feedRef} sx={{ position: "absolute", inset: 0, overflowY: "auto", scrollSnapType: "y mandatory", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          {!clips.length && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f", px: "24px", textAlign: "center" }}>
              <img src={logoSite} alt="" width={32} style={{ opacity: 0.85 }} />
              <Typography sx={{ fontFamily: F, fontSize: "12px", color: "#9aa0b4" }}>{err ? "Couldn't load the sample reel." : "Loading the reel…"}</Typography>
            </Box>
          )}
          {feedList.map((c, i) => (
            <Box
              key={c.id}
              ref={(el: HTMLDivElement | null) => { if (el) cardEls.current.set(c.id, el); else cardEls.current.delete(c.id); }}
              sx={{ position: "relative", height: "100%", scrollSnapAlign: "start", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end", p: "14px", pb: "22px", background: "#08070b" }}
            >
              <video
                ref={(el) => { if (el) videoEls.current.set(c.id, el); else videoEls.current.delete(c.id); }}
                src={videoUrl}
                poster={c.thumb || undefined}
                muted={muted}
                playsInline
                preload="none"
                onClick={(e) => { const v = e.currentTarget; if (v.paused) v.play().catch(() => { /* ignore */ }); else v.pause(); }}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  if (v.currentTime >= c.end - 0.05) {
                    if (inReelMode && feedList.length > 1) {
                      // stitched-reel: auto-advance to the next pick (wraps to the top)
                      const next = (i + 1) % feedList.length;
                      try { v.pause(); } catch (err2) { /* ignore */ }
                      v.currentTime = c.start;
                      const h = feedRef.current?.clientHeight || screenH;
                      try { feedRef.current?.scrollTo({ top: next * h, behavior: "smooth" }); } catch (err2) { /* ignore */ }
                    } else {
                      v.currentTime = c.start;
                    }
                  }
                  const p = progressEls.current.get(c.id);
                  if (p) { const pct = ((v.currentTime - c.start) / Math.max(0.1, c.end - c.start)) * 100; p.style.width = `${Math.min(100, Math.max(0, pct))}%`; }
                }}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", cursor: "pointer", objectPosition: `${framePos[c.id]?.x ?? 50}% ${framePos[c.id]?.y ?? 50}%` }}
              />
              <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, rgba(8,7,11,0.35) 0%, transparent 30%, transparent 55%, rgba(8,7,11,0.72) 100%)" }} />

              {/* drag-to-reposition overlay */}
              {reframeId === c.id && (
                <Box
                  onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => { try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err2) { /* ignore */ } dragFrom.current = { x: e.clientX, y: e.clientY }; }}
                  onPointerMove={(e: React.PointerEvent<HTMLDivElement>) => {
                    const d = dragFrom.current;
                    if (!d) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const dx = ((e.clientX - d.x) / rect.width) * 100;
                    const dy = ((e.clientY - d.y) / rect.height) * 100;
                    dragFrom.current = { x: e.clientX, y: e.clientY };
                    setFramePos((p) => { const cur = p[c.id] || { x: 50, y: 50 }; return { ...p, [c.id]: { x: Math.min(100, Math.max(0, cur.x - dx)), y: Math.min(100, Math.max(0, cur.y - dy)) } }; });
                  }}
                  onPointerUp={() => { dragFrom.current = null; }}
                  sx={{ position: "absolute", inset: 0, zIndex: 4, touchAction: "none", cursor: "grab", "&:active": { cursor: "grabbing" }, border: `2px dashed ${CHERRY}aa` }}
                >
                  <Typography sx={{ position: "absolute", top: 30, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", fontFamily: MONO, fontSize: "8.5px", color: "#fff", background: "rgba(0,0,0,0.6)", borderRadius: "6px", px: "8px", py: "4px", pointerEvents: "none" }}>DRAG TO REPOSITION · TAP ✓ WHEN DONE</Typography>
                </Box>
              )}

              {/* progress */}
              <Box sx={{ position: "absolute", top: 12, left: 14, right: 14, height: 2, borderRadius: "999px", background: "rgba(255,255,255,0.16)", overflow: "hidden" }}>
                <Box ref={(el: HTMLDivElement | null) => { if (el) progressEls.current.set(c.id, el); else progressEls.current.delete(c.id); }} sx={{ height: "100%", width: 0, background: "rgba(255,255,255,0.75)" }} />
              </Box>

              {/* right rail */}
              <Box sx={{ position: "absolute", right: 10, bottom: 84, display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", zIndex: 5 }}>
                {railBtn(liked.has(c.id), () => like(c.id), <FavoriteIcon sx={{ fontSize: 18 }} />, fmt(64 + ((c.id * 37) % 900) + (liked.has(c.id) ? 1 : 0)))}
                {railBtn(picked.has(c.id), () => pick(c.id), <img src={logoSite} alt="" width={17} style={{ display: "block", filter: picked.has(c.id) ? "none" : "brightness(0) invert(1)" }} />, picked.has(c.id) ? "Picked" : "CherryPick")}
                {railBtn(reframeId === c.id, () => setReframeId((cur) => (cur === c.id ? null : c.id)), reframeId === c.id ? <Typography sx={{ fontSize: "15px", lineHeight: 1 }}>✓</Typography> : <OpenWithIcon sx={{ fontSize: 17 }} />, reframeId === c.id ? "Done" : "Reframe")}
              </Box>

              {/* caption */}
              <Box sx={{ maxWidth: 200, zIndex: 2 }}>
                <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "14.5px", lineHeight: 1.5, color: "#fff", display: "inline", background: "rgba(0,0,0,0.42)", borderRadius: "6px", px: "6px", py: "2px", boxDecorationBreak: "clone" as "clone", WebkitBoxDecorationBreak: "clone" as "clone" }}>
                  {c.text}
                </Typography>
                <Typography sx={{ fontFamily: F, fontSize: "10px", color: "#d5d9e6", mt: "8px" }}>Made with 🍒 CherryClipz · clip {i + 1}/{feedList.length}</Typography>
              </Box>

              {/* swipe hint on card 1 (only in browse mode) */}
              {i === 0 && feedList.length > 1 && !inReelMode && (
                <Box sx={{ position: "absolute", bottom: 12, left: "50%", ml: "-24px", width: 48, textAlign: "center", opacity: 0.75, pointerEvents: "none", "@keyframes ccFeedBob": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(-6px)" } }, animation: "ccFeedBob 1.7s ease-in-out infinite" }}>
                  <KeyboardArrowUpIcon sx={{ fontSize: 20, color: "#fff", display: "block", mx: "auto" }} />
                  <Typography sx={{ fontFamily: MONO, fontSize: "8.5px", color: "#fff", mt: "-3px" }}>swipe</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* reel-mode banner */}
        {inReelMode && (
          <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 8, fontFamily: MONO, fontSize: "8.5px", letterSpacing: "0.5px", color: "#fff", background: `${CHERRY}dd`, borderRadius: "6px", px: "7px", py: "4px" }}>
            YOUR PICKS — PLAYING AS ONE REEL
          </Box>
        )}

        {/* sound toggle */}
        {clips.length > 0 && (
          <Box component="button" onClick={toggleMute} title={muted ? "Unmute" : "Mute"} sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, cursor: "pointer", width: 34, height: 34, borderRadius: "999px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: "14px", p: 0, display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { borderColor: CHERRY } }}>
            {muted ? "🔇" : "🔊"}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FlagshipFeedPhone;
